// External Imports
import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';

import Chart from 'react-apexcharts';

import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

import AccessDenied from './AccessDenied';
import ActivateVoucher from './ActivateVoucher';
import QuotaRefresh from './alerts/QuotaRefresh';
import ShowVolumePeriods from './ShowVolumePeriods';

//Hooks
import { useAuth } from 'react-oidc-context';
import useToken from '../utils/useToken';
import useBearerToken from '../utils/useBearerToken';
import ForbiddenError from '../utils/error';

import { fetchSiteQuotaStatus } from '../utils/site';

export default function VolumeQuota({ site, subscriptions }) {
  const auth = useAuth();
  const { token, isValidToken } = useToken();
  const { isLoggedIn, getToken } = useBearerToken();

  // Local Data
  const [error, setError] = useState({ message: '', severity: 'primary', access_denied: false });
  const [loading, setLoading] = useState(true);
  const [autoReloadQuota, setAutoReloadQuota] = useState(false);

  const [voucherActivation, setVoucherActivation] = useState('');

  const [subsStatus, setSubsStatus] = useState([]);
  const [utilisation, setUtilisationChart] = useState({});

  const formatGB = (gb) => {
    if (gb < 1) {
      return Math.round(gb * 1000) + ' MB';
    }
    return Math.round(gb * 10) / 10 + ' GB';
  };

  const getSiteQuota = async (customerId, siteId) => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    setLoading(true);

    fetchSiteQuotaStatus(customerId, siteId, bearerToken)
      .then((data) => {
        const filtered = Object.fromEntries(
          Object.entries(data.periods).filter((entry) => {
            return data.periods[entry[0]].length > 0;
          }),
        );

        setUtilisationChart(
          Object.fromEntries(
            Object.keys(filtered).map((subnet) => {
              const current_period = data.periods[subnet].at(-1);
              const available_gb = current_period.allowance_gb - current_period.utilisation_gb;
              return [
                subnet,
                {
                  options: {
                    chart: {
                      id: 'utilisation-' + subnet,
                      toolbar: {
                        show: true,
                      },
                    },
                    title: {
                      text: 'Current Utilisation',
                    },
                    labels: ['Consumed', 'Available'],
                    dataLabels: {
                      enabled: false,
                    },
                    yaxis: {
                      labels: {
                        formatter: formatGB,
                      },
                    },
                  },
                  series: [current_period.utilisation_gb, available_gb > 0 ? available_gb : 0],
                },
              ];
            }),
          ),
        );

        setSubsStatus(data);
        setError({ message: '', severity: 'success', access_denied: false });
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);

        if (error instanceof ForbiddenError) {
          setError({ message: '', severity: 'danger', access_denied: true });
        } else {
          setError({
            message: 'Could not retrieve site quota status!',
            severity: 'danger',
            access_denied: false,
          });
        }
      });
  };

  useEffect(() => {
    if (site.site_id) {
      getSiteQuota(site.customer_id, site.site_id);
    }
  }, [site]);

  // Event Handlers
  const resetFap = async () => {
    setVoucherActivation('reset');
  };

  const addQuota = async () => {
    setVoucherActivation('quota');
  };

  const topupQuota = async () => {
    setVoucherActivation('topup');
  };

  const hideActivation = async (tf) => {
    setVoucherActivation('');

    if (tf) {
      setAutoReloadQuota(true);
    }
  };

  const reloadQuota = async (tf) => {
    if (tf) {
      setAutoReloadQuota(false);
    } else {
      getSiteQuota(site.customer_id, site.site_id);
    }
  };

  return (
    <>
      {subscriptions.filter(
        (subscription) =>
          subscription.rate_plan.type == 'fap' ||
          subscription.rate_plan.type == 'quota' ||
          subscription.rate_plan.type == 'quota_inclusive',
      ).length == 0 ? (
        <Alert variant='success'>
          <span>
            This site is only subscribed to uncapped services.
            <br />
            There is no limit on the volume being consumed.
          </span>
        </Alert>
      ) : (
        <>
          {autoReloadQuota && <QuotaRefresh site={site} reloadFunction={reloadQuota} />}
          <>
            <Row className='mb-3'>
              <Col>
                <Card>
                  <Card.Header>Volume Based Plans</Card.Header>
                  <Card.Body>
                    {loading ? (
                      <Alert variant='warning'>
                        <Spinner animation='border' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </Spinner>
                        <span>Data loading...</span>
                      </Alert>
                    ) : (
                      <>
                        {error.message != '' ? (
                          <Alert variant={error.severity}>
                            <span>{error.message}</span>
                          </Alert>
                        ) : (
                          <>
                            {error.access_denied ? (
                              <AccessDenied />
                            ) : (
                              <Table striped>
                                <thead>
                                  <tr>
                                    <th>Subnet</th>
                                    <th>Quota Type</th>
                                    <th>Quota Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {subsStatus.current_quota_status && (
                                    <>
                                      {subscriptions
                                        .filter(
                                          (subscription) =>
                                            subscription.rate_plan.type == 'fap' ||
                                            subscription.rate_plan.type == 'quota' ||
                                            subscription.rate_plan.type == 'quota_inclusive',
                                        )
                                        .map((subscription) => (
                                          <Fragment key={subscription.subnet}>
                                            <tr
                                              key={subscription.subnet}
                                              className={
                                                subsStatus.current_quota_status[
                                                  subscription.subnet
                                                ] == 'normal'
                                                  ? 'table-success'
                                                  : 'table-danger'
                                              }
                                            >
                                              <td>{subscription.subnet}</td>
                                              <td>
                                                {subscription.rate_plan.type == 'quota' && (
                                                  <span>Load volume by voucher</span>
                                                )}
                                                {subscription.rate_plan.type ==
                                                  'quota_inclusive' && (
                                                  <span>Limited quota with top-up</span>
                                                )}
                                                {subscription.rate_plan.type == 'fap' && (
                                                  <span>
                                                    Fair Access Policy:{' '}
                                                    {subscription.rate_plan.fap.quota_gb} GB&nbsp;
                                                    {subscription.rate_plan.fap.reset_term ==
                                                      'daily' && <>per day</>}
                                                    {subscription.rate_plan.fap.reset_term ==
                                                      'monthly' && <>per 30 days</>}
                                                    {subscription.rate_plan.fap.reset_term ==
                                                      'monthly_on_1st' && <>month</>}
                                                  </span>
                                                )}
                                              </td>
                                              <td>
                                                {subscription.rate_plan.type == 'fap' && (
                                                  <>
                                                    {subsStatus.current_quota_status[
                                                      subscription.subnet
                                                    ] == 'normal' ? (
                                                      <>The subscription is performing as normal.</>
                                                    ) : (
                                                      <>
                                                        The bandwidth is restricted due to excess
                                                        utilisation.
                                                      </>
                                                    )}
                                                  </>
                                                )}
                                                {subscription.rate_plan.type == 'quota' && (
                                                  <>
                                                    {subsStatus.current_quota_status[
                                                      subscription.subnet
                                                    ] == 'normal' ? (
                                                      <>
                                                        The subscription is active (volume loaded).
                                                      </>
                                                    ) : (
                                                      <>
                                                        The subscription is inactive (no quota
                                                        available).
                                                      </>
                                                    )}
                                                  </>
                                                )}
                                                {subscription.rate_plan.type ==
                                                  'quota_inclusive' && (
                                                  <>
                                                    {subsStatus.current_quota_status[
                                                      subscription.subnet
                                                    ] == 'normal' ? (
                                                      <>You are within your allowed quota.</>
                                                    ) : (
                                                      <>
                                                        The subscription is inactive (no quota
                                                        available).
                                                      </>
                                                    )}
                                                  </>
                                                )}
                                              </td>
                                            </tr>
                                            {subsStatus.current_quota_status[subscription.subnet] !=
                                              'normal' && (
                                              <tr key='{subscription.subnet}-quota'>
                                                <td>
                                                  {subscription.rate_plan.type == 'fap' && (
                                                    <Button
                                                      variant='success'
                                                      className='me-2'
                                                      value={true}
                                                      onClick={resetFap}
                                                    >
                                                      Reset
                                                    </Button>
                                                  )}
                                                  {subscription.rate_plan.type == 'quota' && (
                                                    <Button
                                                      variant='success'
                                                      className='me-2'
                                                      value={true}
                                                      onClick={addQuota}
                                                    >
                                                      Add Quota
                                                    </Button>
                                                  )}
                                                  {subscription.rate_plan.type ==
                                                    'quota_inclusive' && (
                                                    <Button
                                                      variant='success'
                                                      className='me-2'
                                                      value={true}
                                                      onClick={topupQuota}
                                                    >
                                                      Top Up Volume
                                                    </Button>
                                                  )}
                                                </td>
                                                <td>&nbsp;</td>
                                                <td>&nbsp;</td>
                                              </tr>
                                            )}
                                          </Fragment>
                                        ))}
                                    </>
                                  )}
                                </tbody>
                              </Table>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {loading ? (
              <Alert variant='warning'>
                <Spinner animation='border' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </Spinner>
                <span>Data loading...</span>
              </Alert>
            ) : (
              <>
                {error.message == '' && (
                  <>
                    {subscriptions
                      .filter(
                        (subscription) =>
                          subscription.rate_plan.type == 'fap' ||
                          subscription.rate_plan.type == 'quota' ||
                          subscription.rate_plan.type == 'quota_inclusive',
                      )
                      .map((quotaSub) => (
                        <Row key={quotaSub.subnet}>
                          <Col sm={6}>
                            <Card>
                              <Card.Header>Utilisation</Card.Header>
                              <Card.Body>
                                {utilisation[quotaSub.subnet] && (
                                  <Chart
                                    type='pie'
                                    options={utilisation[quotaSub.subnet].options}
                                    series={utilisation[quotaSub.subnet].series}
                                    height='300px'
                                  />
                                )}
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col sm={6}>
                            <Card>
                              <Card.Header>Last 12 Billing Cycles</Card.Header>
                              <Card.Body>
                                {subsStatus.current_quota_status && (
                                  <ShowVolumePeriods
                                    periods={subsStatus.periods[quotaSub.subnet]}
                                  />
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      ))}

                    {voucherActivation != '' && (
                      <ActivateVoucher
                        show={voucherActivation != ''}
                        type={voucherActivation}
                        site={site}
                        closeFunction={hideActivation}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        </>
      )}
    </>
  );
}

VolumeQuota.propTypes = {
  site: PropTypes.object,
  subscriptions: PropTypes.array,
};
